import { type FC, type ReactNode, useState } from 'react';
import { graphql, useFragment, useRelayEnvironment, useMutation } from 'react-relay';
import cn from 'classnames';
import dibsCss from 'dibs-css';

import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import HeartOutlined from 'dibs-icons/exports/legacy/HeartOutlined';
import HeartFilled from 'dibs-icons/exports/legacy/HeartFilled';
import InfoCircle from 'dibs-icons/exports/InfoCircle';
import Diamond from 'dibs-icons/exports/Diamond';
import Medal from 'dibs-icons/exports/Medal';
import Vetting from 'dibs-icons/exports/Vetting';
import Recognition from 'dibs-icons/exports/Recognition';
import {
    goldSeller,
    platinumSeller,
    diamondSeller,
    vettedSeller,
    goldTooltip,
    platinumTooltip,
    diamondTooltip,
    vettedTooltip,
    goldSellerTitle,
    platinumSellerTitle,
    diamondSellerTitle,
    recognizedSeller,
    recognizedDescription,
} from '../messages/pdpDistinctionsMessages';
import { SharedTooltip } from '../../shared/SharedTooltip';
import { PdpSharedMeetTheSellerHeader } from './PdpSharedMeetTheSellerHeader';
import { PdpSharedSellerInfo } from '../shared/PdpSharedSellerInfo/PdpSharedSellerInfo';
import { PdpSharedContactSellerButton } from '../shared/PdpSharedContactSellerButton';
import { getBuyerId } from 'dibs-cookie-jar';
import { followSellerMutation as followSeller } from '../../mutations/followSeller';
import { usePdpRespMarketplaceLayoutPrimaryContext } from '../respMarketplace/PdpRespMarketplaceLayout/PdpRespMarketplaceLayoutPrimaryContextProvider';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import { type PdpSharedMeetTheSeller_seller$key } from './__generated__/PdpSharedMeetTheSeller_seller.graphql';
import { type PdpSharedMeetTheSeller_user$key } from './__generated__/PdpSharedMeetTheSeller_user.graphql';
import { type PdpSharedMeetTheSeller_item$key } from './__generated__/PdpSharedMeetTheSeller_item.graphql';
import { type followSellerMutation } from '../../mutations/__generated__/followSellerMutation.graphql';

type Props = {
    seller: PdpSharedMeetTheSeller_seller$key;
    user: PdpSharedMeetTheSeller_user$key | null | undefined;
    item: PdpSharedMeetTheSeller_item$key;
    isMobile?: boolean;
};

const sellerFragment = graphql`
    fragment PdpSharedMeetTheSeller_seller on Seller {
        ...PdpSharedMeetTheSellerHeader_seller
        ...PdpSharedSellerInfo_seller
        isAccessible
        serviceId
        favoritesByUser(userIds: $userId) @include(if: $fetchUser) {
            serviceId
        }
        sellerProfile {
            company
        }
        isDistinguished
        isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
        sellerTiers {
            isGoldTier
            isPlatinumTier
            sellerTierDataNotFound
        }
        orderFeedbackPdp {
            metadata {
                aggregatedFeedback {
                    publicNoteTotalResults
                }
            }
        }
    }
`;

const userFragment = graphql`
    fragment PdpSharedMeetTheSeller_user on User {
        canShowSellerName
        ...PdpSharedContactSellerButton_user
        serviceId
    }
`;

const itemFragment = graphql`
    fragment PdpSharedMeetTheSeller_item on Item {
        ...PdpSharedContactSellerButton_item
    }
`;

export const PdpSharedMeetTheSeller: FC<Props> = ({
    seller: sellerRef,
    user: userRef,
    item: itemRef,
    isMobile = false,
}) => {
    const environment = useRelayEnvironment();
    const seller = useFragment(sellerFragment, sellerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const { currency } = usePdpRespMarketplaceLayoutPrimaryContext();
    const {
        orderFeedbackPdp,
        serviceId,
        favoritesByUser,
        isRewardsTier2,
        sellerTiers,
        isDistinguished,
    } = seller || {};
    const isNoReviews = !orderFeedbackPdp?.metadata?.aggregatedFeedback?.publicNoteTotalResults;
    const favoriteId = favoritesByUser?.[0]?.serviceId || null;
    const currentUserId = user?.serviceId || '';

    const [commitFollow, isInFlight] = useMutation<followSellerMutation>(followSeller);

    const handleFollowClick = async (): Promise<void> => {
        if (!currentUserId) {
            const res = await authModalLoader.show({
                flow: authModalLoader.constants.DEFAULT_FLOW,
                relayEnvironment: environment,
            });
            if (res?.err) {
                return;
            }
        }
        const resolvedUserId = getBuyerId(document.cookie) || currentUserId;

        commitFollow({
            variables: {
                userId: resolvedUserId,
                input: {
                    userId: resolvedUserId,
                    sellerPk: serviceId ?? '',
                    favoriteId: favoriteId ?? undefined,
                },
            },
        });
    };

    const [isInfoHovered, setIsInfoHovered] = useState(false);
    const [isRecognizedInfoHovered, setIsRecognizedInfoHovered] = useState(false);

    if (!seller?.isAccessible) {
        return null;
    }

    const sellerCompany = (
        <FormattedMessage
            id="PdpSharedMeetTheSeller.meetTheSeller"
            defaultMessage="Meet the {sellerCompany, select, null {Seller} other {{sellerCompany}}}"
            values={{
                sellerCompany: user?.canShowSellerName ? seller?.sellerProfile?.company : null,
            }}
        />
    );

    const { sellerTierDataNotFound, isGoldTier, isPlatinumTier } = sellerTiers || {};
    const sellerTierDataFound = !sellerTierDataNotFound;
    const isGoldSeller = sellerTierDataFound && isGoldTier;
    const isPlatinumSeller = sellerTierDataFound && isPlatinumTier;

    let sellerTitle = vettedSeller;
    let tooltipDescription = vettedTooltip;
    let Icon: ReactNode = <Vetting variation="20" />;
    let className = dibsCss.fillShagreen800;
    let tier = null;

    if (isGoldSeller) {
        Icon = <Medal variation="20" />;
        sellerTitle = goldSeller;
        tooltipDescription = goldTooltip;
        className = dibsCss.fillYellow700;
        tier = goldSellerTitle;
    } else if (isPlatinumSeller) {
        Icon = <Medal variation="20" />;
        sellerTitle = platinumSeller;
        tier = platinumSellerTitle;
        className = dibsCss.fillSatan;
        tooltipDescription = platinumTooltip;
    } else if (isRewardsTier2) {
        Icon = <Diamond variation="20" />;
        tier = diamondSellerTitle;
        sellerTitle = diamondSeller;
        tooltipDescription = diamondTooltip;
    }

    const ctaSection = (
        <>
            <PdpSharedContactSellerButton
                item={item}
                user={user}
                currency={currency}
                fullWidth
                size="medium"
                dataTn="meet-the-seller-contact"
                launchOrigin="pdp - meet the seller"
            >
                <FormattedMessage
                    id="PdpSharedMeetTheSeller.messageTheSeller"
                    defaultMessage="Message The Seller"
                />
            </PdpSharedContactSellerButton>
            <div className={dibsCss.mtXsmall}>
                <Button
                    fullWidth
                    size="medium"
                    type="transparent"
                    dataTn="pdp-follow-dealer-cta"
                    onClick={handleFollowClick}
                    disabled={isInFlight}
                >
                    <span
                        className={cn(dibsCss.inlineFlex, dibsCss.itemsCenter, dibsCss.gapXsmall)}
                    >
                        {favoriteId ? (
                            <>
                                <HeartFilled className={cn(dibsCss.w20px, dibsCss.h20px)} />
                                <FormattedMessage
                                    id="PdpSharedMeetTheSeller.following"
                                    defaultMessage="Following"
                                />
                            </>
                        ) : (
                            <>
                                <HeartOutlined className={cn(dibsCss.w20px, dibsCss.h20px)} />
                                <FormattedMessage
                                    id="PdpSharedMeetTheSeller.follow"
                                    defaultMessage="Follow Seller"
                                />
                            </>
                        )}
                    </span>
                </Button>
            </div>
        </>
    );

    return (
        <>
            <div
                className={cn(dibsCss.mbMedium, dibsCss.sassyFontHeaderLarge, {
                    [dibsCss.mtLarger]: isNoReviews && !isMobile,
                    [dibsCss.mbSmall]: isNoReviews && isMobile,
                })}
            >
                {sellerCompany}
            </div>
            {isNoReviews ? (
                <section
                    className={cn(
                        dibsCss.roundedXxsmall,
                        dibsCss.bgBuyertertiary,
                        dibsCss.pMedium,
                        dibsCss.mbSmall,
                        dibsCss.flex,
                        isMobile ? dibsCss.flexCol : dibsCss.flex,
                        isMobile ? dibsCss.itemsStart : dibsCss.itemsCenter,
                        dibsCss.justifyBetween,
                        className,
                        { [dibsCss.gapMedium]: !isMobile, [dibsCss.gapYXsmall]: isMobile }
                    )}
                >
                    <div>
                        <PdpSharedMeetTheSellerHeader
                            seller={seller}
                            variant="noReviews"
                            isMobile={isMobile}
                        />
                    </div>

                    <div
                        className={cn(dibsCss.bgDolphin, dibsCss.selfStretch, dibsCss.flexNone, {
                            [dibsCss.w1px]: !isMobile,
                            [dibsCss.h1px]: isMobile,
                        })}
                        aria-hidden="true"
                        role="separator"
                    />

                    <div>
                        <PdpSharedSellerInfo
                            seller={seller}
                            showLocation
                            orderQuantityLocation="top"
                            showOneLineEstablishment
                            displayZeroResponseTime
                        />
                    </div>
                    <div
                        className={cn({
                            [dibsCss.mtSmall]: isMobile,
                            [dibsCss.wFull]: isMobile,
                            [dibsCss.mlMedium]: !isMobile,
                        })}
                        style={{ minWidth: isMobile ? undefined : '360px' }}
                    >
                        {ctaSection}
                    </div>
                </section>
            ) : (
                <section
                    className={cn(
                        dibsCss.roundedXxsmall,
                        dibsCss.bgBuyertertiary,
                        dibsCss.pMedium,
                        dibsCss.maxW400px,
                        dibsCss.mbSmall,
                        className
                    )}
                >
                    <div
                        className={cn(
                            dibsCss.borderBSolid,
                            dibsCss.borderB,
                            dibsCss.borderBDolphin,
                            dibsCss.pbSmall,
                            dibsCss.mbSmall
                        )}
                    >
                        <PdpSharedMeetTheSellerHeader seller={seller} />
                    </div>

                    <div className={cn(dibsCss.flex, dibsCss.itemsCenter, dibsCss.wFull)}>
                        <span
                            className={cn(
                                dibsCss.inlineFlex,
                                dibsCss.itemsCenter,
                                dibsCss.gapXsmall
                            )}
                        >
                            <span className={cn(dibsCss.w20px, dibsCss.h20px)}>{Icon}</span>
                            <span className={dibsCss.sassyFontBodyMediumHeavy}>{sellerTitle}</span>
                        </span>
                        <SharedTooltip
                            ariaLabel="Seller tier info"
                            dataTn="pdp-meet-the-seller-tier-info"
                            align="center"
                            direction="top"
                            closeOnOutsideClick
                            linkClass={cn(dibsCss.inlineFlex, dibsCss.itemsCenter)}
                            wrapperClass={cn(dibsCss.mlAuto, dibsCss.flex)}
                            tooltipContent={
                                tier ? (
                                    <FormattedMessage
                                        id="PdpSharedMeetTheSeller.tierTooltip"
                                        defaultMessage="{tier}: {description}"
                                        values={{
                                            tier: (
                                                <span className={dibsCss.sassyFontBodySmallHeavy}>
                                                    {tier}
                                                </span>
                                            ),
                                            description: tooltipDescription,
                                        }}
                                    />
                                ) : (
                                    tooltipDescription
                                )
                            }
                            onVisibilityChange={isVisible => {
                                if (!isVisible) {
                                    setIsInfoHovered(false);
                                }
                            }}
                        >
                            <span
                                className={dibsCss.fillNoir}
                                onMouseEnter={() => setIsInfoHovered(true)}
                                onMouseLeave={() => setIsInfoHovered(false)}
                            >
                                <InfoCircle variation={isInfoHovered ? '24Filled' : '24'} />
                            </span>
                        </SharedTooltip>
                    </div>

                    {isDistinguished && (
                        <div
                            className={cn(
                                dibsCss.flex,
                                dibsCss.itemsCenter,
                                dibsCss.wFull,
                                dibsCss.mtXsmall
                            )}
                        >
                            <span
                                className={cn(
                                    dibsCss.inlineFlex,
                                    dibsCss.itemsCenter,
                                    dibsCss.gapXsmall
                                )}
                            >
                                <span
                                    className={cn(
                                        dibsCss.w20px,
                                        dibsCss.h20px,
                                        dibsCss.fillSuccesssecondary
                                    )}
                                >
                                    <Recognition variation="20" />
                                </span>
                                <span className={dibsCss.sassyFontBodyMediumHeavy}>
                                    {recognizedSeller}
                                </span>
                            </span>
                            <SharedTooltip
                                ariaLabel="Recognized seller info"
                                dataTn="pdp-meet-the-seller-recognized-info"
                                align="center"
                                direction="top"
                                closeOnOutsideClick
                                linkClass={cn(dibsCss.inlineFlex, dibsCss.itemsCenter)}
                                wrapperClass={cn(dibsCss.mlAuto, dibsCss.flex)}
                                tooltipContent={recognizedDescription}
                                onVisibilityChange={isVisible => {
                                    if (!isVisible) {
                                        setIsRecognizedInfoHovered(false);
                                    }
                                }}
                            >
                                <span
                                    className={dibsCss.fillNoir}
                                    onMouseEnter={() => setIsRecognizedInfoHovered(true)}
                                    onMouseLeave={() => setIsRecognizedInfoHovered(false)}
                                >
                                    <InfoCircle
                                        variation={isRecognizedInfoHovered ? '24Filled' : '24'}
                                    />
                                </span>
                            </SharedTooltip>
                        </div>
                    )}

                    <div className={cn(dibsCss.mbSmall, dibsCss.mtXsmall)}>
                        <PdpSharedSellerInfo
                            seller={seller}
                            showLocation
                            orderQuantityLocation="none"
                            showOneLineEstablishment
                            displayZeroResponseTime
                        />
                    </div>

                    {ctaSection}
                </section>
            )}
        </>
    );
};
